import React, { useState, useContext, useEffect } from 'react';
import './TransactionTable.css';
import DataContext from '../../../Context/dataContext';
import axios from 'axios';

// Replace material-ui with MUI
import { 
  Paper, 
  TextField, 
  Select, 
  MenuItem, 
  InputBase,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  TableSortLabel,
  FormControl,
  CircularProgress,
  Button,
  InputAdornment,
  IconButton
} from '@mui/material';

// Import time icon
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const TransactionTable = (props) => {
  const employeeData = useContext(DataContext);
  const [loading, setLoading] = useState(false);
  
  // Initialize rows with status defaulting to 'P' if not provided
  const [rows, setRows] = useState(
    props.data.map(row => ({
      ...row,
      status: row.status || 'P'
    }))
  );
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rows.length > 10 ? 10 : rows.length);
  
  // Sorting state
  const [orderBy, setOrderBy] = useState('SNO');
  const [order, setOrder] = useState('asc');
  
  // Column widths and visibility
  const [columnWidths, setColumnWidths] = useState(
    props.columns.reduce((acc, col) => {
      if (col.name === "attend_code" || col.name === 'emp_code') {
        acc[col.name] = { width: 0, visible: false };
      } else if (col.name === "SNO") {
        acc[col.name] = { width: 110, visible: true };
      } else {
        acc[col.name] = { width: 120, visible: true };
      }
      return acc;
    }, {})
  );
  
  // Row changes for editing
  const [rowChanges, setRowChanges] = useState({});
  
  // Time input references to programmatically control focus
  const timeInputRefs = React.useRef({});
  
  // Function to handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  // Function to handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Function to handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Function to handle cell value change
  const handleCellChange = (rowId, columnName, value) => {
    const newRowChanges = {
      ...rowChanges,
      [rowId]: {
        ...(rowChanges[rowId] || {}),
        [columnName]: value
      }
    };
    
    // Special handling for shift_code changes
    if (columnName === 'shift_code') {
      const findIndex = props.ad.shifts.rows.findIndex(shift => shift.shift_code === value);
      if (findIndex !== -1) {
        const shift = props.ad.shifts.rows[findIndex];
        newRowChanges[rowId]['time_in'] = shift.in_time;
        newRowChanges[rowId]['time_out'] = shift.time_out;
      }
    }
    
    // Calculate working hours and overtime
    updateWorkingHoursAndOvertime(newRowChanges, rowId);
    
    setRowChanges(newRowChanges);
  };
  
  // Function to focus on time input when icon is clicked
  const handleTimeIconClick = (rowId, columnName) => {
    const refKey = `${rowId}-${columnName}`;
    if (timeInputRefs.current[refKey]) {
      timeInputRefs.current[refKey].focus();
      // Set the type to time to show the browser's time picker
      timeInputRefs.current[refKey].type = "time";
    }
  };
  
  // Function to update working hours and overtime
  const updateWorkingHoursAndOvertime = (changes, key) => {
    const rowIndex = rows.findIndex(row => row.SNO === Number(key));
    if (rowIndex === -1) return;
    
    const currentRow = { ...rows[rowIndex], ...(changes[key] || {}) };
    
    // Find the shift
    let findIndex;
    if (currentRow.shift_code) {
      findIndex = props.ad.shifts.rows.findIndex(shift => shift.shift_code === currentRow.shift_code);
    } else {
      findIndex = props.ad.shifts.rows.findIndex(shift => shift.shift_code === rows[rowIndex].shift_code);
    }
    
    if (findIndex === -1) return;
    
    // Get shift times
    const shiftInTime = props.ad.shifts.rows[findIndex].in_time;
    if (!shiftInTime) return;
    
    // Calculate actual standard working hours based on shift
    const shiftInTimeDate = new Date(`2017-05-02T${shiftInTime}`);
    const shiftOutTimeDate = new Date(`2017-05-02T${props.ad.shifts.rows[findIndex].time_out}`);
    const actualHours = (shiftOutTimeDate - shiftInTimeDate) / (1000 * 60 * 60);
    
    // Calculate working hours
    let workingHours = 0;
    if (currentRow.time_in && currentRow.time_out) {
      const timeIn = new Date(`2017-05-02T${currentRow.time_in}`);
      const timeOut = new Date(`2017-05-02T${currentRow.time_out}`);
      workingHours = (timeOut - timeIn) / (1000 * 60 * 60);
    } else if (currentRow.time_in && !currentRow.time_out && rowIndex > 0) {
      const timeIn = new Date(`2017-05-02T${currentRow.time_in}`);
      const timeOut = new Date(`2017-05-02T${rows[rowIndex - 1].time_out}`);
      workingHours = (timeOut - timeIn) / (1000 * 60 * 60);
    } else if (!currentRow.time_in && currentRow.time_out && rowIndex > 0) {
      const timeIn = new Date(`2017-05-02T${rows[rowIndex - 1].time_in}`);
      const timeOut = new Date(`2017-05-02T${currentRow.time_out}`);
      workingHours = (timeOut - timeIn) / (1000 * 60 * 60);
    }
    
    // Update working hours and overtime
    changes[key].no_working_hrs = Math.abs(workingHours);
    changes[key].overtime_hrs = workingHours > actualHours ? Math.abs(workingHours - actualHours) : 0;
  };
  
  // Function to save changes
  const handleSave = () => {
    const postData = Object.keys(rowChanges).map(key => {
      const rowIndex = rows.findIndex(row => row.SNO === Number(key));
      return {
        ...rows[rowIndex],
        ...rowChanges[key]
      };
    });
    
    const newPData = {
      ATTEND_CODE: props.form.ATTEND_CODE || '',
      SITE: props.form.SITE,
      ATTEND_DATE: props.form.ATTEND_DATE,
      DEPARTMENT: props.form.DEPARTMENT || '',
      EMP_TYPE: props.form.EMP_TYPE || '',
    };
    
    setLoading(true);
    
    axios
      .post(
        `${employeeData.URL}/api/v1/dailyattendance/daily-attendance?site=${newPData.SITE.toString()}&date=${newPData.ATTEND_DATE}&empType=${newPData.EMP_TYPE.toString()}&code=${newPData.ATTEND_CODE.toString()}&dept=${newPData.DEPARTMENT.toString()}`,
        postData,
        {
          withCredentials: true,
          credentials: 'include',
        }
      )
      .then((response) => {
        console.log(response);
        setLoading(false);
        // Update the rows with the saved changes
        const updatedRows = rows.map(row => {
          if (rowChanges[row.SNO]) {
            return { ...row, ...rowChanges[row.SNO] };
          }
          return row;
        });
        setRows(updatedRows);
        setRowChanges({});
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
        // Handle error appropriately
      });
  };
  
  // Function to sort rows
  const sortedRows = React.useMemo(() => {
    const comparator = (a, b) => {
      if (order === 'asc') {
        return a[orderBy] < b[orderBy] ? -1 : 1;
      } else {
        return a[orderBy] > b[orderBy] ? -1 : 1;
      }
    };
    
    return [...rows].sort(comparator);
  }, [rows, order, orderBy]);
  
  // Get the current page of rows
  const currentRows = rowsPerPage > 0
    ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedRows;
  
  // Custom cell renderers based on column type
  const renderCell = (row, column) => {
    const value = row[column.name];
    const rowId = row.SNO;
    const isEdited = rowChanges[rowId] && rowChanges[rowId][column.name] !== undefined;
    const displayValue = isEdited ? rowChanges[rowId][column.name] : value;
    const refKey = `${rowId}-${column.name}`;
    
    // For columns that cannot be edited
    if (column.name === 'SNO' || column.name === 'attend_code' || column.name === 'emp_code' || 
        column.name === 'card_no' || column.name === 'employee_name' ||
        column.name === 'no_working_hrs' || column.name === 'overtime_hrs') {
      return <span className="cell-value">{displayValue}</span>;
    }
    
    // For shift_code column
    if (column.name === 'shift_code') {
      return (
        <FormControl fullWidth size="small">
          <Select
            value={displayValue || ''}
            onChange={(e) => handleCellChange(rowId, column.name, e.target.value)}
            input={<InputBase />}
            className="editable-cell shift-select"
          >
            {props.ad.shifts.rows.map(shift => (
              <MenuItem key={shift.shift_code} value={shift.shift_code}>
                {shift.shift_desc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    
    // For status columns
    if (column.name === 'status' || column.name === 'status2') {
      return (
        <FormControl fullWidth size="small">
          <Select
            value={displayValue || ''}
            onChange={(e) => handleCellChange(rowId, column.name, e.target.value)}
            input={<InputBase />}
            className="editable-cell status-select"
          >
            {props.ad.STATUS.rows.map(status => (
              <MenuItem key={status.KEY} value={status.KEY}>
                {status.VALUE}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    
    // For leave period columns
    if (column.name === 'LEAVE_PERIOD' || column.name === 'LEAVE_PERIOD2') {
      return (
        <FormControl fullWidth size="small">
          <Select
            value={displayValue || ''}
            onChange={(e) => handleCellChange(rowId, column.name, e.target.value)}
            input={<InputBase />}
            className="editable-cell leave-period-select"
          >
            {props.ad.LEAVE_PERIOD.rows.map(period => (
              <MenuItem key={period.KEY} value={period.KEY}>
                {period.VALUE}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    
    // For time columns with improved time picker functionality
    if (column.name === 'time_in' || column.name === 'time_out' || column.name === 's_time_in' || column.name === 's_time_out') {
      return (
        <TextField
          inputRef={el => timeInputRefs.current[refKey] = el}
          type="text" // Initially set to text to show the current value
          value={displayValue || ''}
          onChange={(e) => handleCellChange(rowId, column.name, e.target.value)}
          onClick={() => {
            // Change input type to time when clicked
            if (timeInputRefs.current[refKey]) {
              timeInputRefs.current[refKey].type = "time";
            }
          }}
          onBlur={() => {
            // Change input type back to text when focus is lost
            if (timeInputRefs.current[refKey]) {
              timeInputRefs.current[refKey].type = "text";
            }
          }}
          className="editable-cell time-input"
          size="small"
          InputProps={{
            disableUnderline: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => handleTimeIconClick(rowId, column.name)}
                >
                  <AccessTimeIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      );
    }
    
    // For LEAVE_TYPE columns
    if (column.name === 'LEAVE_TYPE' || column.name === 'LEAVE_TYPE2') {
      return (
        <InputBase
          value={displayValue || ''}
          onChange={(e) => handleCellChange(rowId, column.name, e.target.value)}
          className="editable-cell leave-type-input"
        />
      );
    }
    
    // Default for other columns
    return (
      <InputBase
        value={displayValue || ''}
        onChange={(e) => handleCellChange(rowId, column.name, e.target.value)}
        className="editable-cell"
      />
    );
  };
  
  return (
    <div className="transaction-table">
      <div className="save-button-container">
        {loading ? (
          <CircularProgress size={24} className="loading-spinner" />
        ) : (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSave}
            className="save-button"
          >
            SAVE
          </Button>
        )}
      </div>
      
      <Paper elevation={0} className="table-paper">
        <TableContainer className="table-container">
          <Table stickyHeader aria-label="transaction table">
            <TableHead>
              <TableRow>
                {props.columns.map((column) => (
                  columnWidths[column.name].visible && (
                    <TableCell
                      key={column.name}
                      style={{ width: columnWidths[column.name].width }}
                      className="header-cell"
                    >
                      <TableSortLabel
                        active={orderBy === column.name}
                        direction={orderBy === column.name ? order : 'asc'}
                        onClick={() => handleRequestSort(column.name)}
                      >
                        {column.title}
                      </TableSortLabel>
                    </TableCell>
                  )
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentRows.map((row, index) => {
                const isOdd = index % 2 === 1;
                return (
                  <TableRow 
                    key={row.SNO}
                    className={isOdd ? 'striped-row' : ''}
                  >
                    {props.columns.map((column) => (
                      columnWidths[column.name].visible && (
                        <TableCell 
                          key={`${row.SNO}-${column.name}`} 
                          className="data-cell"
                        >
                          {renderCell(row, column)}
                        </TableCell>
                      )
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rows.length > 100 ? [10, 50, 100, { label: 'All', value: -1 }] : 
                             (rows.length > 50 ? [10, 50, { label: 'All', value: -1 }] : 
                             (rows.length > 10 ? [10, { label: 'All', value: -1 }] : [{ label: 'All', value: -1 }]))}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          className="pagination-container"
        />
      </Paper>
    </div>
  );
};

export default TransactionTable;